<template>
  <div class="shop-page">
    <!-- 顶部步骤导航 -->
    <van-steps :active="2" active-color="#ee0a24">
      <van-step>复制商品链接</van-step>
      <van-step>粘贴至输入框</van-step>
      <van-step>找券享优惠</van-step>
    </van-steps>

    <!-- 搜索区域 -->
    <transition name="slide-fade">
      <div class="search-section" v-show="showSearch">
        <van-search
          v-model="searchValue"
          placeholder="搜索商品链接"
          shape="round"
          background="#f7f8fa"
        />
        <div class="clear-search" @click="clearSearch">
          <van-icon name="delete-o" />
          清空搜索
        </div>
        <van-button 
          type="danger" 
          round 
          block 
          class="search-btn"
          @click="handleSearch"
        >
          <van-icon name="coupon-o" />
          找券享优惠
        </van-button>
      </div>
    </transition>

    <!-- 商品分类列表 -->
    <ProductFeed />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { searchProduct } from '@/api';
import ProductFeed from '@/components/ProductFeed.vue';
import { 
  Button, 
  Search, 
  Steps, 
  Step, 
  Icon,
  showToast,
  showFailToast,
  showDialog,
} from 'vant';

export default {
  name: 'Shop',
  components: {
    ProductFeed,
    [Button.name]: Button,
    [Search.name]: Search,
    [Steps.name]: Steps,
    [Step.name]: Step,
    [Icon.name]: Icon,
  },
  setup() {
    const searchValue = ref('');
    const showSearch = ref(true);

    const clearSearch = () => {
      searchValue.value = '';
    };

    const handleSearch = async () => {
      if (!searchValue.value.trim()) {
        showToast('请输入搜索内容');
        return;
      }
      
      const toast = showToast({
        message: '转换中...',
        type: 'loading',
        duration: 0,
        forbidClick: true
      });

      try {
        const formData = new FormData();
        formData.append('project', 'discount_website');
        formData.append('keyword', searchValue.value);

        const response = await searchProduct(formData);

        toast.close();

        if (response.data.code === 200) {
          const targetUrl = response.data.data;
          showDialog({
            title: '检查优惠券',
            message: '点击下方按钮前往优惠页面',
            confirmButtonText: '立即前往',
            showCancelButton: true
          }).then(() => {
            window.open(targetUrl, '_blank');
          }).catch(() => {
          });
        } else {
          showFailToast(response.data.message || '转换失败');
        }
      } catch (error) {
        toast.close();
        showFailToast('网络请求失败');
      }
    };

    // 滚动处理
    let lastScrollTop = 0;
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollDelta = scrollTop - lastScrollTop;
      
      // 向下滑动 (scrollTop 增加) 且距离顶部超过 50px
      if (scrollDelta > 0 && scrollTop > 50) {
        showSearch.value = false;
      } 
      // 向上滑动 (scrollTop 减少) 且变化量大于 10px (防止微小抖动)
      else if (scrollDelta < -10 || scrollTop <= 0) {
        showSearch.value = true;
      }
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    };

    const SUPPORTED_JD_DOMAINS = ['jd.com', 'jingxi.com', '3.cn', 'u.jd.com'];

    const isJdLink = (text) => {
       const escapedDomains = SUPPORTED_JD_DOMAINS.map(domain => domain.replace(/\./g, '\\.'));
       const jdPattern = new RegExp(`(${escapedDomains.join('|')})`, 'i');
       return jdPattern.test(text);
    };

    onMounted(() => {
      window.addEventListener('scroll', handleScroll);
      
      // 剪贴板读取
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText().then(text => {
          if (text && text.trim() && isJdLink(text)) {
            searchValue.value = text.trim();
          }
        }).catch(err => {
          console.log('Clipboard access failed or denied', err);
        });
      }
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    return {
      searchValue,
      showSearch,
      clearSearch,
      handleSearch,
    };
  }
};
</script>

<style scoped>
.shop-page {
  background: #f7f8fa;
  min-height: 100vh;
  /* padding-bottom 由外部 TabBar 决定，但为了防止内容被遮挡，可以在这里加 padding 或 marginBottom */
  padding-bottom: 20px; 
}

/* 步骤导航 */
:deep(.van-steps--horizontal) {
  padding: 12px 10px;
  background: #fff;
}

:deep(.van-step__title) {
  font-size: 12px;
  color: #646566;
}

:deep(.van-step__circle) {
  width: 16px;
  height: 16px;
}

/* 搜索区域 */
.search-section {
  background: #fff;
  padding: 10px 12px;
  margin-bottom: 1px;
  position: sticky;
  top: 0;
  z-index: 99;
  box-shadow: 0 2px 10px rgba(0,0,0,0.03);
}

/* 搜索框样式优化 */
:deep(.van-search) {
  padding: 0;
  margin-bottom: 10px;
}

:deep(.van-search__content) {
  background: #f7f8fa;
  border-radius: 18px;
}

.clear-search {
  display: flex;
  align-items: center;
  justify-content: flex-end; /* 靠右显示 */
  color: #999;
  font-size: 12px;
  margin: 4px 0 10px;
  cursor: pointer;
  padding: 0 4px;
}

.clear-search .van-icon {
  margin-right: 4px;
  font-size: 14px;
}

.search-btn {
  height: 40px;
  font-size: 15px;
  font-weight: 500;
  border-radius: 20px;
  background: linear-gradient(90deg, #ff6034, #ee0a24);
  border: none;
  box-shadow: 0 4px 10px rgba(238, 10, 36, 0.3); /* 按钮阴影 */
}

.search-btn .van-icon {
  margin-right: 6px;
  font-size: 16px;
}

/* 搜索栏隐藏动画 */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease-out;
  max-height: 100px; /* 足够容纳搜索栏的高度 */
  opacity: 1;
  overflow: hidden;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-100%);
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
